import React, { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import Card from '../../../components/ui/Card';
import { translateTerm as translateTermUtil, fmtBRL } from '../utils';

export default function TransactionsTab({ lang, year, petrobrasData, refDate, t }) {
  const translateTerm = (term) => translateTermUtil(term, lang);

  const [transSearch, setTransSearch] = useState('');

  const transactionSource = useMemo(() => {
    const rows = petrobrasData['transacao_parte_relacionada'] || [];
    return { rows, fallbackYear: null, refDate: refDate };
  }, [petrobrasData, refDate]);

  const filteredTransactions = useMemo(() => {
    if (!transSearch.trim()) return transactionSource.rows;
    const term = transSearch.toLowerCase();
    return transactionSource.rows.filter(tx =>
      (tx.Parte_Relacionada && tx.Parte_Relacionada.toLowerCase().includes(term)) ||
      (tx.Relacao_Emissor && tx.Relacao_Emissor.toLowerCase().includes(term)) ||
      (tx.Objeto_Contrato && tx.Objeto_Contrato.toLowerCase().includes(term))
    );
  }, [transSearch, transactionSource.rows]);

  return (
    <Card
      title={t.card_transactions}
      subtitle={t.label_trans_limit}
      refDate={transactionSource.refDate || refDate}
    >
      {transactionSource.fallbackYear && (
        <p className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 mb-4">
          {lang === 'pt'
            ? `Transações com partes relacionadas não reportadas para ${year}. Exibindo dados mais recentes disponíveis (${transactionSource.fallbackYear}).`
            : `Related-party transactions not reported for ${year}. Showing most recent available data (${transactionSource.fallbackYear}).`}
        </p>
      )}

      <div className="relative mb-4">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" size={16} />
        <input
          type="text"
          value={transSearch}
          onChange={(e) => setTransSearch(e.target.value)}
          placeholder={t.search_placeholder_trans}
          className="w-full bg-[var(--accent-soft)] border border-[var(--border)] rounded-xl py-2.5 pl-10 pr-4 text-sm text-[var(--text)] placeholder-[var(--muted)] focus:outline-none focus:border-emerald-500 transition-colors"
        />
      </div>

      <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
        <table className="w-full text-left border-collapse text-[10px] min-w-[800px]">
          <thead>
            <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
              <th className="p-3.5 font-semibold">{t.th_related_party}</th>
              <th className="p-3.5 font-semibold">{t.th_relation}</th>
              <th className="p-3.5 font-semibold">{t.th_object}</th>
              <th className="p-3.5 font-semibold">{t.th_amount_involved}</th>
              <th className="p-3.5 font-semibold">{t.th_balance}</th>
              <th className="p-3.5 font-semibold">{t.th_duration_trans}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--border)]">
            {filteredTransactions.length > 0 ? (
              filteredTransactions.slice(0, 25).map((tx, idx) => (
                <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                  <td className="p-3.5 font-bold text-[var(--text)]">{translateTerm(tx.Parte_Relacionada)}</td>
                  <td className="p-3.5">
                    <span className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                      {translateTerm(tx.Relacao_Emissor)}
                    </span>
                  </td>
                  <td className="p-3.5 text-[var(--muted)] max-w-xs truncate" title={translateTerm(tx.Objeto_Contrato)}>{translateTerm(tx.Objeto_Contrato)}</td>
                  <td className="p-3.5 font-bold text-emerald-500">{fmtBRL(tx.Montante_Envolvido)}</td>
                  <td className="p-3.5 text-[var(--text)]">{fmtBRL(tx.Saldo_Existente)}</td>
                  <td className="p-3.5 text-[var(--muted)] max-w-xs truncate" title={translateTerm(tx.Duracao_Transacao)}>{translateTerm(tx.Duracao_Transacao)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="p-8 text-center text-[var(--muted)]">{t.l_empty}</td>
              </tr>
            )}
            {filteredTransactions.length > 25 && (
              <tr>
                <td colSpan="6" className="p-4 text-center font-semibold text-yellow-500 bg-[var(--accent-soft)]/10">
                  {t.l_showing_top_25.replace('{total}', filteredTransactions.length)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
